import { Link } from 'react-router-dom';
import type { ArticleCard } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { formatDate } from '@/lib/format';
import { ArticleImage } from '@/components/content';
import { Icon } from '@/components/shell';

/**
 * The top story — PRD §6's lead, above everything else on the page.
 *
 * It is deliberately *not* the hero carousel. The hero is an arrangement of five or six
 * stories curated in the homepage manager, rotating every few seconds; this is the one
 * story the desk is leading with, flagged on the article itself (`articles.is_top_story`)
 * from the article editor. A reader arriving mid-rotation can miss any given hero slide.
 * Nobody misses this.
 *
 * Because it is one story and never moves, it can do what a carousel cannot: load its
 * image eagerly, hold still while it is read, and give the page a first heading that is
 * the same one a crawler and a screen-reader user find.
 *
 * Dark, against the hero's light panel directly below it, so the two never read as one
 * undifferentiated block of lead stories. The two arbitrary greys are the hero's own —
 * text over a photograph is the one place this design steps outside the token palette,
 * and having two values for it rather than four is the discipline that matters.
 */
export function TopStoryBand({ article }: { article: ArticleCard }) {
  const { locale, t } = useLocale();

  return (
    <section className="bg-soft px-gutter pt-5.5" data-top-story={article.id}>
      <Link
        to={article.path}
        className="group grid overflow-hidden rounded-sm bg-ink md:grid-cols-[1.15fr_1fr]"
      >
        <div className="relative overflow-hidden">
          <ArticleImage
            image={article.image}
            alt={article.headline}
            priority
            aspect="aspect-[16/9]"
            sizes="(max-width: 720px) 100vw, 55vw"
            placeholderSeed={article.category.slug}
            className="h-full w-full transition duration-500 group-hover:scale-[1.03]"
          />
          {/* Only on the narrow layout, where the text sits *over* the picture rather
              than beside it. */}
          <span
            aria-hidden="true"
            className="absolute inset-0 bg-[linear-gradient(0deg,rgba(17,20,24,.92),transparent_60%)] md:hidden"
          />
        </div>

        <div className="relative -mt-14 px-5 pb-6 md:mt-0 md:flex md:flex-col md:justify-center md:px-8 md:py-8">
          {/*
            A red chip rather than red text. `--red` on `--ink` is 4.07:1, which is under
            the AA floor for 12px type — `check-contrast.mjs` says so — while white on
            `--red` is 4.66:1 and is already the site's button pairing.
          */}
          <p className="mb-3">
            <span className="inline-block rounded-sm bg-red px-2 py-1 text-xs font-extrabold uppercase tracking-wider text-white">
              {t('section.topStory')}
            </span>
          </p>
          {/*
            Clamped and a step smaller in Assamese, for the reason the hero documents: a
            Bengali-script headline runs about a third longer than its English counterpart
            at the same point size.
          */}
          <h2 className="line-clamp-4 text-2xl font-extrabold leading-tight text-white lg:text-3xl [:lang(en)_&]:lg:text-4xl">
            <span className="group-hover:underline">{article.headline}</span>
          </h2>
          {article.summary && (
            <p className="mt-3 line-clamp-3 text-md text-[#e6e8ea]">{article.summary}</p>
          )}
          <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-[#cfd2d6]">
            {/*
              The category name without its colour. A category's colour is chosen by an
              editor against the white card it normally sits on (PRD §8), and eight of
              the eleven seeded ones fall under 3:1 on this dark panel — `#8a1116` is
              1.9:1. There is no correct automatic answer, so the one dark surface on the
              site does not ask the question.
            */}
            <span className="text-2xs font-extrabold uppercase tracking-wider text-white">
              {article.category.name}
            </span>
            <span>{formatDate(article.publishedAt, locale)}</span>
            <span aria-hidden="true">·</span>
            <span>{t('meta.readTime', { minutes: article.readingMinutes })}</span>
            <span className="ml-auto inline-flex items-center gap-1.5 font-bold text-white">
              {t('action.readFullStory')}
              <Icon name="arrow-right" size={13} />
            </span>
          </div>
        </div>
      </Link>
    </section>
  );
}
